<template>
    <v-carousel
        v-if="blocks.length"
        v-model="model"
        class="hero-carousel-content-blocks"
        :height="cssHeightValue"
        :cycle="!paused && blocks.length > 1"
        :interval="interval"
        :show-arrows="blocks.length > 1"
        :hide-delimiters="blocks.length <= 1"
        hide-delimiter-background
    >

        <template #prev="{ props: slotProps }">
            <SliderButton
                type="prev"
                alt="Previous Slide"
                v-bind="slotProps"
                @mouseenter="togglePause(true)"
                @mouseleave="togglePause(false)"
            ></SliderButton>
        </template>
        <template #next="{ props: slotProps }">
            <SliderButton
                type="next"
                alt="Next Slide"
                v-bind="slotProps"
                @mouseenter="togglePause(true)"
                @mouseleave="togglePause(false)"
            ></SliderButton>
        </template>

        <v-carousel-item
            v-for="block in blocks"
            :key="`hero-carousel-block-${block.uid}`"
        >
            <BlockRenderer
                :model-value="block"
                full-bleed
            ></BlockRenderer>
        </v-carousel-item>
    </v-carousel>
</template>

<script lang="ts" setup>
    import type {
        ContentBlockRenderedListItem
    } from '~/types';
    import SliderButton from '~/components/carousel/SliderButton/SliderButton.vue';
    import BlockRenderer from '~/components/content/blocks/v2/BlockRenderer/BlockRenderer.vue';
    import {
        useContentBlocks
    } from '~/composables';

    const props = withDefaults(
        defineProps<{
            cssHeightValue?: string;
            placement?: string;
        }>(),
        {
            cssHeightValue: '100vh',
            placement: 'hero_carousel_item'
        }
    );

    const { getContentBlocksByPlacement } = useContentBlocks();

    const model = ref<number>(0);
    const paused = ref<boolean>(false);
    const interval = ref<number>(10000);

    const { data } = await useAsyncData(
        `hero-carousel-content-blocks-${props.placement}`,
        () => getContentBlocksByPlacement(props.placement)
    );

    // Skip payloads BlockRenderer cannot bind (missing/unknown template ref)
    // so no blank slides render.
    const blocks = computed<ContentBlockRenderedListItem[]>(() =>
        (data.value?.blocks ?? []).filter((block) => !!block.template?.ref)
    );

    async function togglePause(val: boolean): Promise<void> {
        await nextTick();
        paused.value = val;
    }
</script>

<style lang="scss">
.hero-carousel-content-blocks {
    .slide-move-btn {
        opacity: 0.1;
        transition: opacity 0.4s ease;
    }
    &:hover {
        .slide-move-btn {
            opacity: 0.4;
            transition: opacity 0.4s ease;
        }
    }

    .v-window__left,
    .v-window__right {
        top: calc(50% - 49px);
    }

    .content-block-v2 {
        max-width: none;
    }
}

// Override ContentBlocks
.layout-desktop {
    .hero-carousel-content-blocks {
        h2.content-block-text-title {
            font-size: 3rem !important;
            font-weight: 400;
            line-height: 3.125rem;
            letter-spacing: normal !important;
        }
    }
}
.layout-mobile {
    .hero-carousel-content-blocks {
        .v-window__controls {
            display: none !important;
        }
    }
}
</style>
